import React from "react";
import { currencyFormat } from "../../helpers.js";
import ProgramChecklistPreview from "../ProgramChecklistPreview.jsx";

export const hasDisplayValue = (value) =>
  value !== null && value !== undefined && value !== "" && value !== "n/a";

export const ContextInlineRow = ({ label, value }) => {
  if (!hasDisplayValue(value)) return null;
  return (
    <div className="flex items-baseline gap-1 text-xs leading-snug">
      <span className="shrink-0 text-gray-500">{label}</span>
      <span className="text-gray-300" aria-hidden="true">
        –
      </span>
      <span className="min-w-0 text-gray-900">{value}</span>
    </div>
  );
};

export const ContextBlockRow = ({ label, value }) => {
  if (!hasDisplayValue(value)) return null;
  return (
    <div className="space-y-0.5">
      <dt className="text-[11px] font-medium text-gray-500">{label}</dt>
      <dd className="whitespace-pre-line text-xs leading-snug text-gray-900">
        {value}
      </dd>
    </div>
  );
};

export const ContextEmptyState = ({ message }) => (
  <p className="text-xs text-gray-400">{message}</p>
);

export const GRANT_ELIGIBILITY_COMPACT_FIELDS = [
  { key: "Eligible_Industry", label: "Industries" },
  { key: "Eligible_Region", label: "Region" },
  { key: "Eligible_Employees", label: "Min. Employees" },
  { key: "Eligible_Founded", label: "Required Years In Operation" },
  { key: "Eligible_Revenue", label: "Min. Revenue" },
  { key: "Eligible_Profitable", label: "Requires Profitable" },
];

export const GRANT_ELIGIBILITY_DETAIL_FIELDS = [
  { key: "Eligibility_Criteria", label: "Eligibility Criteria" },
  { key: "Eligible_Activities", label: "Eligible Activities" },
  { key: "Eligible_Applicants", label: "Eligible Applicants" },
];

export function GrantEligibilityPanel({ grant, currencyFormat: formatCurrency }) {
  const formatValue = formatCurrency || currencyFormat;

  if (!grant || grant === "other" || grant === "vendor") {
    return (
      <ContextEmptyState message="Select a grant to see eligibility requirements." />
    );
  }

  const fields = grant.fields || {};
  const compactRows = GRANT_ELIGIBILITY_COMPACT_FIELDS.filter(({ key }) =>
    hasDisplayValue(fields[key])
  ).map(({ key, label }) => {
    let value = fields[key];
    if (key === "Eligible_Revenue") {
      value = formatValue(value);
    }
    return <ContextInlineRow key={key} label={label} value={value} />;
  });

  if (compactRows.length === 0) {
    return (
      <ContextEmptyState message="No structured eligibility requirements for this grant." />
    );
  }

  return <dl className="space-y-1">{compactRows}</dl>;
}

export function GrantEligibilityDetailsPanel({ grant }) {
  if (!grant || grant === "other" || grant === "vendor") {
    return null;
  }

  const fields = grant.fields || {};
  const detailRows = GRANT_ELIGIBILITY_DETAIL_FIELDS.filter(({ key }) =>
    hasDisplayValue(fields[key])
  ).map(({ key, label }) => (
    <ContextBlockRow key={key} label={label} value={fields[key]} />
  ));

  if (detailRows.length === 0) {
    return null;
  }

  return (
    <div className="rounded-xl border border-gray-100 bg-white p-3">
      <dl className="space-y-2">{detailRows}</dl>
    </div>
  );
}

export function CompanyContextPanel({ company, currencyFormat: formatCurrency }) {
  const formatValue = formatCurrency || currencyFormat;

  if (!company?.fields) {
    return (
      <ContextEmptyState message="Select a company to see profile details." />
    );
  }

  const fields = company.fields;
  const locationParts = [
    fields.Address,
    fields.ProvinceOrState,
    fields.Country,
  ].filter(hasDisplayValue);

  return (
    <dl className="space-y-1">
      <ContextInlineRow
        label="Company"
        value={fields.Name || fields.CompanyName}
      />
      <ContextInlineRow
        label="Annual Revenue"
        value={
          hasDisplayValue(fields.AnnualRevenue)
            ? formatValue(fields.AnnualRevenue)
            : null
        }
      />
      <ContextInlineRow
        label="Location"
        value={locationParts.length > 0 ? locationParts.join(", ") : null}
      />
      <ContextInlineRow label="Industry" value={fields.Industry} />
      <ContextInlineRow
        label="Operational Costs"
        value={
          hasDisplayValue(fields.OperationalCosts)
            ? formatValue(fields.OperationalCosts)
            : null
        }
      />
      <ContextInlineRow label="Employees" value={fields.Employees} />
      <ContextInlineRow label="Founded" value={fields.Founded} />
      <ContextInlineRow label="Profitable?" value={fields["Profitable?"]} />
      <ContextInlineRow
        label="Annual Recurring Revenue"
        value={
          hasDisplayValue(fields.AnnualRecurringRevenue)
            ? formatValue(fields.AnnualRecurringRevenue)
            : null
        }
      />
    </dl>
  );
}

export function EligibilityContextView({
  grant,
  company,
  currencyFormat: formatCurrency,
  showProgramChecklist = true,
}) {
  if (!grant) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-sm font-medium text-gray-900">No grant linked</p>
        <p className="mt-1 text-sm text-gray-500">
          Link a grant to this opportunity to view eligibility requirements.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="rounded-xl border border-gray-100 bg-white p-3">
          <h4 className="mb-1.5 text-xs font-semibold text-gray-900">
            Grant eligibility
          </h4>
          <GrantEligibilityPanel
            grant={grant}
            currencyFormat={formatCurrency}
          />
        </div>

        <div className="rounded-xl border border-gray-100 bg-white p-3">
          <h4 className="mb-1.5 text-xs font-semibold text-gray-900">
            Company profile
          </h4>
          <CompanyContextPanel
            company={company}
            currencyFormat={formatCurrency}
          />
        </div>
      </div>

      <GrantEligibilityDetailsPanel grant={grant} />

      {showProgramChecklist &&
        grant !== "other" &&
        grant !== "vendor" && (
          <div className="rounded-xl border border-gray-100 bg-white p-3">
            <h4 className="mb-1.5 text-xs font-semibold text-gray-900">
              Program checklist
            </h4>
            <ProgramChecklistPreview grant={grant} compact />
          </div>
        )}
    </div>
  );
}
